// Petits utilitaires de l'éditeur de story plein écran (téléphone / app native).

export const newId = () => Math.random().toString(36).slice(2, 9);
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export const DRAFT_KEY_FS = "yalola.story_draft_fs";
export const IDENTITY_TF = { scale: 1, x: 0, y: 0 };
export const clampOffset = (x, y, scale) => {
  const m = (scale - 1) * 50;
  return { x: clamp(x, -m, m), y: clamp(y, -m, m) };
};

// Palette de dessin / texte (cf. design_guidelines.json).
export const DRAW_PALETTE = ["#FFFFFF", "#000000", "#F97316", "#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#FDE047"];

// Outils de dessin : épaisseur min/max en px (à l'échelle du cadre affiché).
export const DRAW_TOOLS = [
  { key: "pen", min: 3, max: 12 },
  { key: "marker", min: 14, max: 32 },
  { key: "neon", min: 4, max: 14 },
  { key: "eraser", min: 12, max: 44 },
];
export const strokeWidthPx = (toolKey, thickness) => {
  const tl = DRAW_TOOLS.find((d) => d.key === toolKey) || DRAW_TOOLS[0];
  return tl.min + clamp(thickness, 0, 1) * (tl.max - tl.min);
};

// Texte noir sur pastille claire, blanc sur pastille sombre.
export const contrastText = (color) => {
  const m = /^#([0-9a-f]{3,8})$/i.exec(color || "");
  if (!m) return "#FFFFFF";
  let h = m[1];
  if (h.length < 6) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#111111" : "#FFFFFF";
};

// Pastille de la bulle de texte : none (texte nu) · solid (pastille de la couleur, texte contrasté) · semi (voile noir).
export const PILL_MODES = ["none", "solid", "semi"];
export const pillModeOf = (s) => (!s?.bg ? "none" : s.bg.startsWith("rgba") ? "semi" : "solid");
export const baseColorOf = (s) => (!s ? "#FFFFFF" : pillModeOf(s) === "solid" ? s.bg : s.color || "#FFFFFF");
export const applyPill = (mode, color) =>
  mode === "solid" ? { color: contrastText(color), bg: color }
    : mode === "semi" ? { color, bg: "rgba(0,0,0,0.45)" }
      : { color, bg: "" };

// Heure / date locales pour le sticker « Heure / Date ».
export const localeOf = (lang) => ({ fr: "fr-FR", en: "en-GB", es: "es-ES", pt: "pt-PT" }[lang] || "fr-FR");
export const fmtStoryTime = (lang) => new Date().toLocaleTimeString(localeOf(lang), { hour: "2-digit", minute: "2-digit" });
export const fmtStoryDate = (lang) => new Date().toLocaleDateString(localeOf(lang), { weekday: "long", day: "numeric", month: "long" });

// Texte du verset dans la langue du membre (repli français).
export const verseText = (v, lang) => (v ? (v[`text_${lang}`] || v.text_fr || v.text || "") : "");

// Géométrie des gestes (glisser / pincer / tourner).
export const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
export const angleDeg = (a, b) => (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
export const centroid = (pts) => ({ x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length });
// Aimantation douce à 0° / ±90° / 180°.
export const snapAngle = (deg) => {
  let d = ((deg + 180) % 360 + 360) % 360 - 180;
  for (const a of [-180, -90, 0, 90, 180]) if (Math.abs(d - a) < 5) d = a === -180 ? 180 : a;
  return d;
};

// Fond d'une story texte (dégradé ou image).
export const bgStyleOf = (bg, gradientCss) => (bg && bg.startsWith("/verse-bg/")
  ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
  : { background: gradientCss(bg) || "linear-gradient(160deg,#0F2027,#2C5364)" });

// ISO UTC → valeur d'un <input type="datetime-local"> (heure locale).
export const isoToLocalInput = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Annonce programmée → état de l'éditeur plein écran. Un texte principal (fait sur PC) devient une bulle.
export const editorStateFromStory = (s) => {
  const isText = s.media_type === "text";
  const stickers = Array.isArray(s.stickers) ? s.stickers.map((st) => ({ ...st })) : [];
  if (isText && s.layout !== "free" && (s.text || "").trim()) {
    // Lot 713 — texte principal de plus de 300 caractères → « texte long » (centré, ajusté au cadre) plutôt qu'une bulle débordante.
    const long = s.text.trim().length > 300;
    stickers.unshift({ id: newId(), type: "text", text: s.text.trim(), color: "#FFFFFF", bg: "", font: s.font || undefined, align: "center", x: 0.5, y: 0.5, rotation: 0,
      scale: long ? 0.11 : 0.07 * (typeof s.text_scale === "number" ? s.text_scale : 1), ...(long ? { long: true } : {}) });
  }
  return {
    stage: isText ? "text" : "media",
    media: !isText && s.media_url ? { file: null, localUrl: null, url: s.media_url, media_type: s.media_type, poster_url: s.poster_url || null, progress: 100, status: "done" } : null,
    bg: s.bg || "navy",
    transform: s.media_transform || IDENTITY_TF,
    stickers,
    scheduledAt: s.scheduled_at ? isoToLocalInput(s.scheduled_at) : "",
    drawingUrl: s.drawing_url || null,
  };
};
